import { useEffect, useMemo, useRef, useState } from "react";
import type { KatalogEintrag } from "../lib/wizard";
import { einheitLabel, passtAufSuche, suchform } from "../lib/labels";
import { eur } from "../hooks/useVertraege";

/**
 * Eingabefeld für einen Leistungstitel mit Katalogsuche.
 *
 * Niemand kann eine Katalogbezeichnung zeichengleich raten — „Heckenschnitt bis
 * 2 m Höhe" trifft man nicht blind. Deshalb sucht schon das Tippen im Katalog:
 * unscharf gegenüber Groß-/Kleinschreibung und Umlauten („gruenschnitt" findet
 * „Grünschnitt"), und in beliebiger Wortreihenfolge.
 *
 * Zwei Ausgänge, damit die Eingabe nie in einer Sackgasse endet:
 * - ein Treffer wird übernommen (`onWaehlen`) — samt Satz und Einheit,
 * - oder der getippte Text wird eine eigene Leistung (`onEigene`).
 */
export default function LeistungCombo({
  wert, katalog, onText, onWaehlen, onEigene, platzhalter, autoFocus, eigeneBeschriftung,
}: {
  wert: string;
  katalog: KatalogEintrag[];
  /** Freitext übernehmen (Titel der Position ändern). Fehlt er, ist das Feld
   *  reines Suchfeld und behält seinen Text nicht. */
  onText?: (text: string) => void;
  onWaehlen: (k: KatalogEintrag) => void;
  onEigene?: (titel: string) => void;
  platzhalter?: string;
  autoFocus?: boolean;
  eigeneBeschriftung?: (titel: string) => string;
}) {
  const [offen, setOffen] = useState(false);
  const [markiert, setMarkiert] = useState(0);
  const huelle = useRef<HTMLDivElement>(null);

  const treffer = useMemo(() => {
    const aktive = katalog.filter(k => k.aktiv !== false);
    if (!wert.trim()) return aktive.slice(0, 8);
    return aktive.filter(k => passtAufSuche(`${k.titel} ${k.kategorie}`, wert)).slice(0, 8);
  }, [katalog, wert]);

  // Exakt vorhanden? Dann braucht es kein „als eigene Leistung".
  const schonImKatalog = useMemo(() => {
    const s = suchform(wert);
    return s !== "" && katalog.some(k => suchform(k.titel) === s);
  }, [katalog, wert]);
  const kannEigene = !!onEigene && wert.trim() !== "" && !schonImKatalog;

  useEffect(() => setMarkiert(0), [wert]);

  // Klick daneben schließt die Liste — ohne das bliebe sie über der halben
  // Seite stehen, sobald der Fokus per Maus woanders hinwandert.
  useEffect(() => {
    if (!offen) return;
    const zu = (e: MouseEvent) => {
      if (huelle.current && !huelle.current.contains(e.target as Node)) setOffen(false);
    };
    document.addEventListener("mousedown", zu);
    return () => document.removeEventListener("mousedown", zu);
  }, [offen]);

  function waehlen(k: KatalogEintrag) {
    onWaehlen(k);
    setOffen(false);
  }
  function eigene() {
    onEigene?.(wert.trim());
    setOffen(false);
  }

  const zeilen = kannEigene ? treffer.length + 1 : treffer.length;

  function tastatur(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Escape") { setOffen(false); return; }
    if (!offen && (e.key === "ArrowDown" || e.key === "ArrowUp")) { setOffen(true); return; }
    if (!offen || zeilen === 0) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setMarkiert(m => (m + 1) % zeilen); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setMarkiert(m => (m - 1 + zeilen) % zeilen); }
    else if (e.key === "Enter") {
      e.preventDefault();
      if (markiert < treffer.length) waehlen(treffer[markiert]);
      else if (kannEigene) eigene();
    }
  }

  return (
    <div className="pf-combo" ref={huelle}>
      <input
        className="pf-input is-tight"
        value={wert}
        placeholder={platzhalter || "Leistung suchen oder eintragen…"}
        autoFocus={autoFocus}
        onChange={e => { onText?.(e.target.value); setOffen(true); }}
        onFocus={() => setOffen(true)}
        onKeyDown={tastatur}
        role="combobox"
        aria-expanded={offen}
        aria-autocomplete="list"
      />
      {offen && zeilen > 0 && (
        <div className="pf-combo-liste" role="listbox">
          {treffer.map((k, i) => (
            <button
              key={k.id} role="option" aria-selected={i === markiert}
              className={"pf-combo-zeile" + (i === markiert ? " is-on" : "")}
              // onMouseDown statt onClick: der Klick muss vor dem Fokusverlust
              // greifen, sonst schließt sich die Liste unter dem Finger weg.
              onMouseDown={e => { e.preventDefault(); waehlen(k); }}
              onMouseEnter={() => setMarkiert(i)}
            >
              <span className="pf-combo-titel">{k.titel}</span>
              <span className="pf-combo-preis">
                {eur(k.vk_netto)}/{einheitLabel(k.einheit)}
              </span>
            </button>
          ))}
          {kannEigene && (
            <button
              role="option" aria-selected={markiert === treffer.length}
              className={"pf-combo-zeile is-eigene" + (markiert === treffer.length ? " is-on" : "")}
              onMouseDown={e => { e.preventDefault(); eigene(); }}
              onMouseEnter={() => setMarkiert(treffer.length)}
            >
              <span className="pf-combo-titel">
                ＋ {(eigeneBeschriftung ?? (t => `„${t}" als eigene Leistung`))(wert.trim())}
              </span>
            </button>
          )}
        </div>
      )}
    </div>
  );
}
